# A STALE TAB NOTICES, AND RELOADS ITSELF AT A SAFE MOMENT.
# Cards 6aad9d36bb8767fbeba89f28 (the dashboard trigger) and
# 6aad97d6bb8767fbeba89eee (the mid-game safe points).
#
# WHAT THIS MODULE IS. The decision half, with no opinion about when to act on
# it. `noteServerVersion` is fed by the client API from the X-Client-Version
# header on every response (framework/clientVersionHeader.coffee sets it); the
# app then asks `updateAvailable()` at the moments it knows are safe — after an
# answer is recorded, on a route change, before a new game session — and calls
# `reloadOnce` there. Nothing here reloads on its own, because only the app
# knows what is on the screen.
#
# THE RULES, AND WHY EACH ONE IS A RULE.
#
#   NO STAMP, NO RELOAD. A server that sends no header (an older build, or an
#   instance with no CLIENT_BUILD_INFO row) must read as "nothing to say",
#   never as "you are stale". The same for a page that does not know its own
#   version — a dev build, where `version` is ''. An unstamped side that could
#   trigger a reload is a tab that reloads for ever.
#
#   ONE RELOAD PER VERSION, and the guard is written BEFORE the reload. If the
#   new bundle comes up still believing it is stale — a mis-stamped server, a
#   CDN serving the old file under the new name, a pointed row rolled back — the
#   guard is already set for that version and the second reload never happens.
#   The tab logs a warning instead, which lands in the crash reporter's console
#   tail, so the loop that did not happen is still visible to whoever looks.
#
#   sessionStorage, NOT localStorage. The guard is about THIS tab's boot. A
#   second tab opened on the old bundle deserves its own one reload.
#
#   STORAGE IS ALLOWED TO BE ABSENT. Private windows, blocked site data and
#   embedded WebViews all throw on access. A tab that cannot remember is given
#   its reload — being one version behind is worse than one extra reload — and
#   the same ONE_PER_LOAD in-memory latch below keeps it from looping inside a
#   single page life.

GUARD_PREFIX = 'schemaClientReload:'

# In-memory backstop: whatever storage does, one page life reloads at most once.
reloadedThisLoad = false

bundleVersion = null
serverVersion = null

# What the page was told to load, at render time. `clientVersion` first so an
# app can name it unambiguously; `version` is what the shells have carried for
# years. Empty on a dev build, which is what turns the whole mechanism off.
readBundleVersion = (win) ->
  cfg = win?.config
  v = cfg?.clientVersion ? cfg?.version
  if typeof v == 'string' and v.length then v else null

readGuard = (win, key) ->
  try win?.sessionStorage?.getItem(key) ? null
  catch e then null

writeGuard = (win, key) ->
  try
    win?.sessionStorage?.setItem key, '1'
    true
  catch e
    false

# Fed by the client API from every response. A header that is absent leaves the
# last known value alone rather than clearing it: a single response served by a
# pod mid-restart must not un-stale a tab that is genuinely behind.
noteServerVersion = (value, win = (if typeof window != 'undefined' then window else null)) ->
  bundleVersion ?= readBundleVersion(win)
  serverVersion = value if typeof value == 'string' and value.length
  serverVersion

# The whole decision, in one place, so every trigger asks the same question.
updateAvailable = (win = (if typeof window != 'undefined' then window else null)) ->
  bundleVersion ?= readBundleVersion(win)
  return false unless bundleVersion?          # dev build, or a page that never said
  return false unless serverVersion?          # server said nothing — say nothing back
  serverVersion != bundleVersion

# THE ONE RELOAD-ONCE GUARD. Reload the page, at most once for `subject`, at
# most once per page life. Returns true when the reload was actually started.
#
# `subject` is whatever the reload is FOR — a client version here, a failed
# route chunk in the lazy-route retry (card 6aad9e4dbb8767fbeba89f2c). Both need
# exactly this shape and neither may be allowed to loop a tab, so there is one
# implementation and one place to get it wrong.
reloadOnceFor = (subject, reason = 'client-version', win = (if typeof window != 'undefined' then window else null)) ->
  target = subject ? 'unknown'
  key = "#{GUARD_PREFIX}#{target}"
  if reloadedThisLoad
    console?.warn? "[client version] already reloaded this page life; not reloading again for #{target} (#{reason})"
    return false
  if readGuard(win, key)?
    console?.warn? "[client version] already reloaded once for #{target} and the page still asks — not reloading again (#{reason}). The server may be pointing at something it is not serving."
    return false
  # Guard FIRST: a reload that beats its own bookkeeping is a reload loop.
  writeGuard win, key
  reloadedThisLoad = true
  console?.log? "[client version] reloading for #{target} (#{reason})"
  try
    revalidateThenReload win
  catch e
    return false
  true

# THE RELOAD HAS TO GET PAST THE HTTP CACHE, OR IT IS NOT A RELOAD.
# Card 6ab562ac583dea6548a4ebb5.
#
# `location.reload()` revalidates the DOCUMENT, but a tab that has been open
# for hours is often handed its shell back out of the HTTP cache anyway, so the
# "new" page boots on the SAME bundle it just left. The one-reload-per-version
# guard is written before the reload, so that tab has now spent its single
# reload and will not try again — it logs
#   "already reloaded once for 3.31 ... not reloading again"
# and carries on running 3.29. That line is in a production consoleTail, which
# is how this was proven rather than guessed: the guard doing exactly its job,
# on a reload that never fetched anything new.
#
# So the shell is fetched with `cache: 'reload'` FIRST. That is the one request
# the fetch spec requires to bypass the HTTP cache on the way out and to write
# what comes back INTO it, so the reload that follows is served the new shell
# from cache. Same URL — no `?v=` — deliberately: a cache-busting query string
# makes a second, separate cache entry and leaves the real one stale, so the
# next plain navigation is stale again. This refreshes the entry the tab
# actually uses.
#
# THE GUARD IS UNCHANGED, and so is its ordering. The revalidation is best
# effort in both directions: if `fetch` is missing, if the request fails, or if
# it simply takes too long, the reload happens anyway. A tab that is one
# version behind is worse than a tab that reloads onto the same bundle, and far
# better than a tab that never reloads because a fetch hung.
REVALIDATE_TIMEOUT_MS = 3000

revalidateThenReload = (win) ->
  reload = ->
    try win?.location?.reload()
    catch e then null
  href = win?.location?.href
  fetcher = win?.fetch
  return reload() unless typeof fetcher == 'function' and typeof href == 'string' and href.length
  done = false
  once = ->
    return if done
    done = true
    reload()
  later = win?.setTimeout
  later?.call win, once, REVALIDATE_TIMEOUT_MS
  try
    p = fetcher.call win, href, {cache: 'reload', credentials: 'same-origin'}
    if p?.then?
      p.then once, once
    else
      once()
  catch e
    once()
  true

# Reload for the version we have been told about, at most once for it.
reloadOnce = (reason = 'client-version', win = (if typeof window != 'undefined' then window else null)) ->
  reloadOnceFor (serverVersion ? 'unknown'), reason, win

# Reload if — and only if — there is something to reload for. The shape every
# trigger actually wants.
reloadIfUpdateAvailable = (reason, win = (if typeof window != 'undefined' then window else null)) ->
  return false unless updateAvailable(win)
  reloadOnce reason, win

# THE SAME DECISION, ACTED ON AFTER THE CURRENT TASK FINISHES.
# Card 6aae2221ed08d5788a4e1e14.
#
# `location.reload()` does not stop anything. It schedules a navigation and
# RETURNS, so the rest of the current task — the rest of a boot sequence, the
# rest of a render — runs to completion on the bundle being thrown away. A
# trigger that reads "reloading" as "we are done here" and returns early leaves
# whatever it skipped undone, and the code after it still runs.
#
# That is not a theory. el-feet's dashboard trigger returned before assigning
# the account, boot carried on, and `window.app.account.type` threw: a parent
# on iPhone Safari got the Application Error screen instead of a silent
# refresh, at 11:45 PM, on the first night of a roll. Every stale client would
# have hit it once.
#
# So a trigger that sits INSIDE a sequence uses this instead: finish the work,
# hand the turn back, and let the reload happen from a task of its own. The
# decision is still made now — `updateAvailable` is checked here, synchronously,
# so the caller learns whether a reload is coming and the answer cannot change
# underneath it — and only the act of reloading is deferred. All the guards are
# unchanged, because the reload still goes through reloadOnce.
#
# Returns whether a reload was SCHEDULED, which is not the same claim
# `reloadIfUpdateAvailable` makes (that one returns whether it started). A
# caller must not treat either as "nothing after this line will run".
scheduleReloadIfUpdateAvailable = (reason, win = (if typeof window != 'undefined' then window else null)) ->
  return false unless updateAvailable(win)
  later = win?.setTimeout ? (if typeof setTimeout != 'undefined' then setTimeout else null)
  # No timer to defer with (a non-browser host, a stubbed window): reloading
  # now beats not reloading at all — the caller has already done its work by
  # the time it calls this.
  return reloadOnce(reason, win) unless later?
  later (-> reloadOnce reason, win), 0
  true

state = -> {bundle: bundleVersion, server: serverVersion, reloadedThisLoad: reloadedThisLoad}

# Tests only: this module is deliberately module-level state, because there is
# one page and one answer.
resetForTests = ->
  bundleVersion = null
  serverVersion = null
  reloadedThisLoad = false

module.exports = {
  noteServerVersion, updateAvailable, reloadOnce, reloadOnceFor, reloadIfUpdateAvailable,
  scheduleReloadIfUpdateAvailable,
  state, resetForTests, GUARD_PREFIX, readBundleVersion
}
